import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import { applyActionCode } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db, functions } from '../firebase';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const VerifyEmail = () => {
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('One moment while we confirm your email address.');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oobCode = params.get('oobCode');

    if (!oobCode) {
      setStatus('error');
      setMessage('Invalid verification link. Please request a new email.');
      return;
    }

    const verify = async () => {
      setStatus('verifying');
      setMessage('One moment while we confirm your email address.');
      try {
        await applyActionCode(auth, oobCode);
        const user = auth.currentUser;
        if (user?.reload) {
          try {
            await user.reload();
          } catch (reloadErr) {
            console.warn('VerifyEmail: reload after verification failed:', reloadErr);
          }
        }

        try {
          const markVerified = httpsCallable(functions, 'markEmailVerified');
          await markVerified();
        } catch (fnErr) {
          console.warn('VerifyEmail: backend sync failed, attempting client write:', fnErr);
          try {
            const ref = doc(db, 'users', auth.currentUser?.uid || '');
            if (auth.currentUser?.uid) {
              await updateDoc(ref, { requireEmailReverify: false, emailVerified: true });
            }
          } catch (fallbackErr) {
            console.warn('VerifyEmail: client sync failed:', fallbackErr);
          }
        }
        setStatus('verified');
        setMessage('Your email is confirmed. You may now close this tab and continue shopping.');
      } catch (err) {
        console.error('Email verification failed:', err);
        if (err?.code === 'auth/invalid-action-code' && auth.currentUser) {
          try {
            await auth.currentUser.reload();
            if (auth.currentUser.emailVerified) {
              setStatus('verified');
              setMessage('Your email is confirmed. You may now close this tab and continue shopping.');
              return;
            }
          } catch (reloadErr) {
            console.warn('Link invalid but failed to refresh user:', reloadErr);
          }
        }
        setStatus('error');
        setMessage(
          err?.message ||
            'We could not verify this link. It may be expired or already used. Please request a new email.'
        );
      }
    };

    verify();
  }, []);

  const borderTopColor =
    status === 'verified' ? '#74b816' : status === 'error' ? '#d14343' : '#9ac53f';
  const ringAnimation = status === 'verifying' ? `${spin} 1.4s linear infinite` : 'none';
  const icon =
    status === 'verified' ? '✓' : status === 'error' ? '!' : null;

  return (
    <Box
      sx={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f4f9eb 0%, #ffffff 100%)',
        px: 2,
      }}
    >
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: 4,
          boxShadow: '0 20px 45px rgba(117, 140, 87, 0.15)',
          width: '100%',
          maxWidth: 420,
          textAlign: 'center',
          py: 6,
          px: 4,
        }}
      >
        <Box
          sx={{
            width: 88,
            height: 88,
            borderRadius: '50%',
            border: '6px solid #d6e9b3',
            borderTopColor,
            mx: 'auto',
            mb: 3,
            animation: ringAnimation,
            position: 'relative',
          }}
        >
          {icon && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 36,
                color: status === 'verified' ? '#74b816' : '#d14343',
                fontWeight: 700,
              }}
            >
              {icon}
            </Box>
          )}
        </Box>

        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: '#4f5a38', mb: 1 }}>
          {status === 'verified' ? 'Verified!' : status === 'error' ? 'Unable to verify' : 'Verifying…'}
        </Typography>
        <Typography sx={{ color: '#6d7c4a', fontSize: 16, mb: 3 }}>
          {message}
        </Typography>
        <Typography sx={{ color: '#9aac70', fontSize: 13 }}>
          {status === 'verified'
            ? 'Thanks for helping us keep your account secure.'
            : status === 'error'
            ? 'If this link is expired, please request a new verification email.'
            : 'This only takes a few seconds.'}
        </Typography>
      </Box>
    </Box>
  );
};

export default VerifyEmail;
